import { X, User, Phone, Mail, Calendar, Briefcase, Hash, Info } from 'lucide-react';

export default function TeacherProfileModal({ isOpen, onClose, profile, userRole }) {
  if (!isOpen) return null;

  // Fallback defaults if no profile is available (e.g. for Admin/SuperAdmin without a teacher profile)
  const isTeacher = userRole === 'teacher';
  // profile could be the auth userProfile (which has coreProfile and examProfile) or the coreProfile itself
  const core = profile?.coreProfile || profile || {};
  const exam = profile?.examProfile || {};
  
  const name = 
    core.nameBn || 
    core.teacherNameBn || 
    core.nameEn || 
    core.teacherNameEn || 
    core.name || 
    core.teacherName || 
    exam.teacherName ||
    exam.teacherNameBn ||
    profile?.displayName || 
    'Unknown User';
    
  const designation = core.designation || profile?.designation || (isTeacher ? 'Teacher' : 'Administrator');
  const phone = core.mobile || core.phone || profile?.phone || profile?.mobile || 'N/A';
  const email = core.email || profile?.email || 'N/A';
  const teacherId = core.teacherId || profile?.teacherId || 'N/A';
  const status = core.status || profile?.status || 'active';
  const joinedDate = (core.joiningDate || profile?.joiningDate)
    ? new Date(core.joiningDate || profile.joiningDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'N/A';
  const photoUrl = core.photoUrl || profile?.photoUrl;
  const gender = core.gender || profile?.gender || 'male';

  // Fallback avatar based on gender if no photoUrl
  const defaultAvatar = gender === 'female' 
    ? 'https://api.dicebear.com/7.x/notionists/svg?seed=female&backgroundColor=e2e8f0'
    : 'https://api.dicebear.com/7.x/notionists/svg?seed=male&backgroundColor=e2e8f0';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-slate-900 border border-slate-700/50 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
        
        {/* Header Section */}
        <div className="relative bg-gradient-to-br from-teal-600/20 to-violet-600/20 p-6 flex flex-col items-center border-b border-slate-800">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
          
          <div className="relative w-24 h-24 mb-4 rounded-full p-1 bg-slate-800 border-2 border-slate-700 shadow-lg">
            <img 
              src={photoUrl || defaultAvatar} 
              alt={name} 
              className="w-full h-full object-cover rounded-full bg-slate-800"
              onError={(e) => { e.target.src = defaultAvatar; }}
            />
            {status === 'active' && (
              <span className="absolute bottom-1 right-1 w-4 h-4 bg-emerald-500 border-2 border-slate-900 rounded-full"></span>
            )}
          </div>
          
          <h2 className="text-xl font-bold text-white text-center">{name}</h2>
          <p className="text-sm text-teal-400 font-medium mt-1">{designation}</p>
        </div>

        {/* Details Section */}
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
            <div className="p-2 bg-slate-900 rounded-lg text-slate-400"><Hash size={18} /></div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Teacher ID</p>
              <p className="text-sm text-slate-200 font-medium">{teacherId}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
            <div className="p-2 bg-slate-900 rounded-lg text-slate-400"><Briefcase size={18} /></div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Designation</p>
              <p className="text-sm text-slate-200 font-medium">{designation}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="p-2 bg-slate-900 rounded-lg text-slate-400"><Phone size={18} /></div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Mobile</p>
                <p className="text-sm text-slate-200 font-medium truncate">{phone}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="p-2 bg-slate-900 rounded-lg text-slate-400"><Mail size={18} /></div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Email</p>
                <p className="text-sm text-slate-200 font-medium truncate" title={email}>{email}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
            <div className="p-2 bg-slate-900 rounded-lg text-slate-400"><Calendar size={18} /></div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Joining Date</p>
              <p className="text-sm text-slate-200 font-medium">{joinedDate}</p>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
